import { CreateTopicRequest, FeatureType, ImageUpload, SettingsChangeEvent, TopicCategory, UserTag } from '../../types';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { SettingControlButtons, SettingControlsType } from './setting-control-buttons';
import { customElement, query, state } from 'lit/decorators';

import { StateController } from '../../helpers/state/state-management/state-controller';
import { createTopicRequestFromTopic } from '../../helpers/api/topics-api';
import { heights } from '../../style/variables';
import { isDefined } from '../../helpers/utils';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';

@customElement('setting-controls')
export class SettingControls extends LitElement {
    amplitude_callsite: string = 'Setting Controls';

    @query('#settingControlButtons')
    settingControlButtons?: SettingControlButtons;

    @state()
    isDrawerOpen: boolean = false;

    @state()
    buttonType: SettingControlsType = SettingControlsType.DEFAULT;

    @state()
    label: string = '';

    @state()
    upgradeFeatureType: FeatureType = FeatureType.DuplicateMap;

    @state()
    isDrawerFullScreen: boolean = false;

    @state()
    createTopicRequest: CreateTopicRequest = createTopicRequestFromTopic(topicData.topic);

    @state()
    categories: TopicCategory[] = [];

    @state()
    userTags: UserTag[] = [];

    @state()
    willRefreshMarkers: boolean = false;

    @state()
    willRefreshPage: boolean = false;

    @state()
    imageToUpload?: ImageUpload;


    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);

    openDrawer(e: CustomEvent) {
        this.label = e.detail.label;
        this.isDrawerOpen = true;
        this.imageToUpload = undefined;
    }

    openUpgradeDialog(e: CustomEvent) {
        this.label = 'Upgrade Dialog';
        this.isDrawerOpen = true;
        this.upgradeFeatureType = e.detail.featureType;
    }

    closeUpgradeDialog() {
        this.dispatchEvent(new CustomEvent('closeUpgradeDialog'));
    }

    closeDrawer() {
        this.isDrawerOpen = false;
        this.label = '';
        this.settingControlButtons?.resetActiveButton();
    }

    // remember to bubble all dispatched events that aim to hit this function because they are sometimes rendered inside a slot
    // fire one event per field on topic that needs to change
    async onChange(e: CustomEvent<SettingsChangeEvent>) {
        const updatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        (this.createTopicRequest as any)[fieldToUpdate] = updatedValue;

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }

        if (e.detail.refreshMarkers === true && topicData.topic !== undefined) {
           this.willRefreshMarkers = true;
        }

        if (e.detail.refreshPage === true) {
            this.willRefreshPage = true;
        }
    }

    onChangeHeaderLogoImage(e: CustomEvent) {
        this.imageToUpload = e.detail?.imageToUpload;
    }

    render() {
        return html`
            ${this.isDrawerOpen ?
                html`<setting-drawer
                    .openInitially=${true}
                    title=${this.label === 'Upgrade Dialog' ? `Upgrade`: this.label}
                    .isExpanded=${true}
                    @on-close=${this.closeDrawer}
                    @on-change-value=${this.onChange}
                    @on-change-image="${this.onChangeHeaderLogoImage}"
                >
                        ${this.label === 'Categories & Tags' ? html`
                            <tags-categories-wrapper-mobile></tags-categories-wrapper-mobile>
                        `: this.label === 'Publish to Proxi' ? html`
                            <admin-publish-fragment></admin-publish-fragment>
                        `: this.label === 'Duplicate Map' ? html`
                            <duplicate-map-component></duplicate-map-component>
                        `: this.label === 'Rename Map' ? html`
                            <rename-map></rename-map>
                        `: this.label === 'Header Colors' ? html`
                            <header-colors></header-colors>
                        `: this.label === 'Header Logo' ? html`
                            <header-logo @open-upgrade-dialog=${this.openUpgradeDialog}></header-logo>
                        `: this.label === `Map Style` ? html`
                            <map-orientation></map-orientation>
                            <map-skin></map-skin>
                        `: this.label === `Icon Settings` ? html`
                            <icon-settings></icon-settings>
                        `: this.label === 'Clustering' ? html`
                            <marker-cluster-settings></marker-cluster-settings>
                        `: this.label === 'Point Approval' ? html`
                            <point-approval></point-approval>
                        `: this.label === 'Upvotes' ? html`
                            <allow-upvoting></allow-upvoting>
                        `: this.label === 'Date & Time' ? html`
                            <date-and-time @open-upgrade-dialog=${this.openUpgradeDialog}></date-and-time>
                        `: this.label === 'Custom URL' ? html`
                            <custom-urls></custom-urls>
                        `: this.label === 'Advanced' ? html`
                            <admin-advanced-features-fragment showTabs @open-upgrade-dialog=${this.openUpgradeDialog}></admin-advanced-features-fragment>
                        `: this.label === 'Upgrade Dialog' ? html`
                            <upgrade-dialog .featureType="${this.upgradeFeatureType}"  @close="${() => this.closeUpgradeDialog}"></upgrade-dialog>
                        `: this.label === 'Add Point' ? html`
                            <add-edit-point-component .editMode=${false} hideTitle></add-edit-point-component>
                        `: ''}
                    </setting-drawer>
                `
                : ''
            }
            <div class="setting-controls">
                <setting-control-buttons
                    id="settingControlButtons"
                    .buttonType=${this.buttonType}
                    @on-button-click=${this.openDrawer}
                    @on-upgrade-click=${this.openUpgradeDialog}>
                </setting-control-buttons>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                width: 100%;
            }

            .setting-controls {
                position: absolute;
                width: 100%;
                bottom: 0;
                height: ${unsafeCSS(heights.mobileSettingsControls)};
                background-color: var(--theme-border-color);
                z-index: 10; // always want this to be over the overlayed drawer - TODO - this may still be a problem with settings coming in from nav
            }
        `;
    }
}
